import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, IconButton, Tooltip } from '@mui/material';
import { DataGridPro, GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';
import { Asset, FolderContent } from '@/api/generatedApi';
import { RenderPercentage } from '../Search/SearchTable/cellDefinitions';
import { MatchAsset } from '../Search/SearchTable';
import { ReportBreadcrumbToolbar } from './ReportBreadcrumbToolbar';

const ROW_HEIGHT = 80;

const matchPercentageColumnForGrid: GridColDef = {
  field: 'matchPercentage',
  headerName: 'Match %',
  width: 110,
  minWidth: 90,
  filterable: false,
  sortable: false,
  renderCell: parameters => {
    const matchPercentage = parameters.row.matchPercentage;
    const sourceAsset = parameters.row.sourceAsset;
    if (matchPercentage === undefined) return;
    const matchAsset: MatchAsset = {
      ...parameters.row,
      contentType: 'asset',
      matchPercentage,
    };
    return <RenderPercentage value={matchPercentage} searchAsset={sourceAsset} matchAsset={matchAsset} />;
  },
};

type MatchRow = FolderContent & {
  matchPercentage?: number;
  sourceAsset?: Asset;
};

export const ReportMasterDetail = ({
  assets,
  selectedAssetId,
  groupName,
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  toolbarActions,
  onRowContextMenu,
  onSetAsReference,
}: {
  assets: ReportGroupAsset[];
  selectedAssetId: string | undefined;
  groupName: string;
  columns: GridColDef[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  toolbarActions?: React.ReactNode;
  onRowContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onSetAsReference?: (assetId: string) => void;
}) => {
  const groupAssetIds = new Set(assets.map(a => a.id));
  const effectiveSelectedId = selectedAssetId ?? assets[0]?.id;
  const selectedAsset = assets.find(a => a.id === effectiveSelectedId);

  const matchRows: MatchRow[] = (() => {
    if (!selectedAsset) return [];
    const referenceRow: MatchRow = {
      ...selectedAsset,
      contentType: 'asset' as const,
    };
    const matches = (selectedAsset.matches ?? []).map(m => ({
      ...m.asset,
      contentType: 'asset' as const,
      matchPercentage: m.matchPercentage,
      sourceAsset: selectedAsset,
    }));
    return [referenceRow, ...matches];
  })();

  const setAsReferenceColumn: GridColDef = {
    field: '_setAsReference',
    headerName: '',
    width: 50,
    minWidth: 50,
    maxWidth: 50,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    renderCell: parameters => {
      if (!onSetAsReference || !groupAssetIds.has(parameters.row.id) || parameters.row.id === effectiveSelectedId)
        return;
      return (
        <Tooltip title='Set as reference asset'>
          <IconButton size='small' onClick={() => onSetAsReference(parameters.row.id)}>
            <SwapHorizIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      );
    },
  };

  const gridColumns = (() => {
    const filtered = columns.filter(col => col.field !== 'matchPercentage');
    const base = [filtered[0], matchPercentageColumnForGrid, ...filtered.slice(1)];
    return onSetAsReference ? [setAsReferenceColumn, ...base] : base;
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <ReportBreadcrumbToolbar
        assets={assets}
        selectedAssetId={selectedAssetId}
        groupName={groupName}
        toolbarActions={toolbarActions}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <DataGridPro
          rows={matchRows}
          columns={gridColumns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          rowHeight={ROW_HEIGHT}
          columnHeaderHeight={40}
          density='compact'
          disableRowSelectionOnClick
          disableColumnFilter
          getRowId={row => row.id}
          slotProps={{
            ...(onRowContextMenu && {
              row: { onContextMenu: onRowContextMenu },
            }),
          }}
          sx={{
            height: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              lineHeight: 'unset !important',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none !important',
            },
          }}
        />
      </Box>
    </Box>
  );
};
